import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { useState, useCallback, useEffect } from "react";
import type CancelablePromise from "cancelable-promise";
import { cancelable } from "cancelable-promise";

export function cn(...inputs: ClassValue[]): string {
    return twMerge(clsx(inputs));
}

export interface UseFetchActionParameters<T> {
    (): Promise<T>;
}

export type UseFetchActionReturn<T> = [T | undefined, boolean, Error | undefined, () => void];

export interface UseFetchActionOptions<T> {
    onDone?: (data: T) => void;
    onError?: (error: Error) => void;
}

/**
 * Provides state management and cancelling of Promise based actions, which are triggered on
 * component mount - primarily fetching data for the view.
 *
 * If you want to do something after the action you can either use useEffect on returned
 * values (data, error) or pass `onDone` or `onError` as options.
 *
 * You can refresh the action either by passing different action or using convenient `refresh`
 * function that is returned as 4th element in the array. Note that data is not changed until refresh is finished.
 *
 * Make sure to memorize action.
 *
 * @example
 * const [data, isLoading, error, refresh] = useFetchAction(action);
 * @param action function that will return a Promise
 * @param options additional options
 * @returns an array of [isLoading, data, error, refresh]
 */
export const useFetchAction = <T>(
    action: UseFetchActionParameters<T>,
    options?: UseFetchActionOptions<T>,
): UseFetchActionReturn<T> => {
    const [isLoading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<Error | undefined>();
    const [data, setData] = useState<T | undefined>();
    const [promise, setPromise] = useState<CancelablePromise<T>>();

    const { onDone, onError } = options ?? {};

    const fetch = useCallback(() => {
        const call = cancelable(action());

        setLoading(true);
        setError(undefined);

        call.then((data: T) => {
            setData(data);
            setLoading(false);

            if (onDone) onDone(data);
        }).catch((e: Error) => {
            setError(e);
            setData(undefined);
            setLoading(false);

            if (onError) {
                onError(e);
            } else {
                // We have to throw the error because our auth service listens
                // to the "unhandledrejection" event. If we do not throw here
                // we will not be redirected to the login page on an invalid
                // session.
                throw e;
            }
        });

        return call;
    }, [action, onError, onDone]);

    // Each action will use it's own cancel callback to avoid cancelling 2 calls - old and new one
    useEffect(() => {
        const cancelable = fetch();

        return () => cancelable.cancel();
    }, [fetch]);

    // Manual refreshments will use the same callback as only case where the action can be cancelled is unmount
    // useCallback is used to sometimes avoid perpetual execution fe. when using refresh in useEffect in the child component, since refresh will be as dependency
    const refresh = useCallback(() => {
        const call = fetch();
        setPromise(call);
    }, [fetch]);

    // This provides unmount call for refreshing
    useEffect(() => {
        return () => promise?.cancel();
    }, [promise]);

    return [data, isLoading, error, refresh] as [
        T | undefined,
        boolean,
        Error | undefined,
        () => void,
    ];
};
